import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * EnrichmentContext provides global enrichment state accessible across components.
 *
 * Used by:
 * - EnrichmentProgressPanel (detailed progress display)
 * - EnrichmentGlobalIndicator (header badge)
 * - AdvancedProductsGrid (failed row highlighting)
 */
const EnrichmentContext = createContext({
  activeSkus: [],
  jobs: {},
  isProcessing: false,
  progress: { completed: 0, total: 0, failed: 0 },
  failedSkus: [],
  startEnrichment: () => {},
  updateJobs: () => {},
  clearEnrichment: () => {},
  cancelEnrichment: () => {}
});

export const EnrichmentProvider = ({ children }) => {
  const [activeSkus, setActiveSkus] = useState([]);
  const [jobs, setJobs] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ completed: 0, total: 0, failed: 0 });
  const [failedSkus, setFailedSkus] = useState([]);
  const [cancelCallback, setCancelCallback] = useState(null);

  /**
   * Start tracking a new enrichment batch.
   * @param {string[]} skus - SKUs being enriched
   * @param {Function} cancelFn - Function to call when cancelling
   */
  const startEnrichment = useCallback((skus, cancelFn) => {
    setActiveSkus(skus);
    setIsProcessing(true);
    setJobs({});
    setProgress({ completed: 0, total: skus.length, failed: 0 });
    setFailedSkus([]);
    setCancelCallback(() => cancelFn);
  }, []);

  /**
   * Update jobs state from useEnrichmentProgress hook.
   * Calculates progress and failed SKUs.
   * @param {object} newJobs - Jobs object keyed by SKU
   */
  const updateJobs = useCallback((newJobs) => {
    setJobs(newJobs);

    const jobList = Object.values(newJobs);
    const completed = jobList.filter(j =>
      ['completed', 'partial', 'approved', 'pushed'].includes(j.status)
    ).length;
    const failed = jobList.filter(j =>
      ['failed', 'partial'].includes(j.status)
    );

    setProgress(prev => ({
      ...prev,
      completed,
      failed: failed.length
    }));

    setFailedSkus(failed.map(j => j.sku));

    // Check if all done
    const stillProcessing = jobList.some(j =>
      ['pending', 'processing'].includes(j.status)
    );
    setIsProcessing(stillProcessing);
  }, []);

  /**
   * Clear all enrichment state (dismiss panel).
   */
  const clearEnrichment = useCallback(() => {
    setActiveSkus([]);
    setJobs({});
    setIsProcessing(false);
    setProgress({ completed: 0, total: 0, failed: 0 });
    setFailedSkus([]);
    setCancelCallback(null);
  }, []);

  /**
   * Cancel enrichment (calls the cancel callback).
   */
  const cancelEnrichment = useCallback(async () => {
    if (cancelCallback) {
      await cancelCallback();
    }
    // Mark as not processing but keep failed for highlighting
    setIsProcessing(false);
  }, [cancelCallback]);

  return (
    <EnrichmentContext.Provider value={{
      activeSkus,
      jobs,
      isProcessing,
      progress,
      failedSkus,
      startEnrichment,
      updateJobs,
      clearEnrichment,
      cancelEnrichment
    }}>
      {children}
    </EnrichmentContext.Provider>
  );
};

export const useEnrichment = () => useContext(EnrichmentContext);

export default EnrichmentContext;
